import type { EdgelessGridStyle } from '@blocksuite/affine-block-surface';
import type { EdgelessLineGridPalette } from '@blocksuite/affine-shared/styles';
import {
  CANVAS_WORLD_MAX,
  CANVAS_WORLD_MIN,
  type CanvasWorld,
} from '@blocksuite/store';

import { planEdgelessGrid } from './grid-plan.js';

interface GridViewport {
  width: number;
  height: number;
  left: number;
  top: number;
  gap: number;
  translateX: number;
  translateY: number;
  pixelRatio: number;
}

type LayeredGridViewport = Omit<GridViewport, 'gap'> & { zoom: number };

type GridMarkStyle = Exclude<EdgelessGridStyle, 'lines'>;

/** Lines take one colour per tier; dots and crosses share the grid colour. */
export interface EdgelessGridColors {
  lines: EdgelessLineGridPalette;
  mark: string;
}

// Every tier draws the same mark; only spacing and fade tell the tiers apart.
const DOT_RADIUS_CSS = 1;
const CROSS_ARM_CSS = 3;
const CROSS_WIDTH_CSS = 0.5;

/** The backing frame in physical pixels, aligned to the device pixel grid. */
function physicalFrame(viewport: GridViewport) {
  const { width, height, left, top, gap, pixelRatio } = viewport;
  if (
    !Object.values(viewport).every(Number.isFinite) ||
    width <= 0 ||
    height <= 0 ||
    gap <= 0 ||
    pixelRatio <= 0
  ) {
    return null;
  }
  const originX = left * pixelRatio - Math.floor(left * pixelRatio);
  const originY = top * pixelRatio - Math.floor(top * pixelRatio);
  const pixelWidth = Math.ceil(width * pixelRatio + originX);
  const pixelHeight = Math.ceil(height * pixelRatio + originY);
  return {
    originX,
    originY,
    width: pixelWidth,
    height: pixelHeight,
    position: `${-originX / pixelRatio}px ${-originY / pixelRatio}px`,
    size: `${pixelWidth / pixelRatio}px ${pixelHeight / pixelRatio}px`,
  };
}

/**
 * Exact world positions of one tier along one axis, in physical pixels. Large
 * pans are reduced, the gap is never rounded: each world position is projected
 * on its own and snapped by its caller, so snapping cannot accumulate drift.
 */
function worldPositions(
  extent: number,
  translation: number,
  origin: number,
  gap: number,
  pixelRatio: number,
  reach: number
) {
  const step = gap * pixelRatio;
  const phase = (translation % gap) * pixelRatio + origin;
  const first = Math.floor((-reach - phase) / step);
  const count = Math.ceil((extent + 2 * reach) / step) + 2;
  return Array.from(
    { length: count },
    (_, index) => phase + (first + index) * step
  );
}

/** All geometry is in physical pixels; only the final CSS placement is scaled. */
export function calculateLineGridGeometry(
  viewport: GridViewport,
  lineWidthCss = 0.5
) {
  if (!Number.isFinite(lineWidthCss) || lineWidthCss <= 0) return null;
  const frame = physicalFrame(viewport);
  if (!frame) return null;
  const { gap, translateX, translateY, pixelRatio } = viewport;
  // Round coverage up, then compensate with alpha so fractional DPRs preserve
  // each tier's intended visual weight without antialiased/subpixel edges.
  const lineWidth = Math.max(1, Math.ceil(lineWidthCss * pixelRatio));
  const starts = (extent: number, translation: number, origin: number) =>
    worldPositions(extent, translation, origin, gap, pixelRatio, 0)
      .map(center => Math.round(center - lineWidth / 2))
      .filter(position => position < extent && position + lineWidth > 0);
  return {
    width: frame.width,
    height: frame.height,
    lineWidth,
    // Keep the thin grid's visual weight without relying on subpixel strokes.
    opacity: Math.min(1, (lineWidthCss * pixelRatio) / lineWidth),
    x: starts(frame.width, translateX, frame.originX),
    y: starts(frame.height, translateY, frame.originY),
    position: frame.position,
    size: frame.size,
  };
}

/** Mark centres of one tier: every crossing of its world lines. */
export function calculateGridMarkGeometry(
  viewport: GridViewport,
  style: GridMarkStyle
) {
  const frame = physicalFrame(viewport);
  if (!frame) return null;
  const { gap, translateX, translateY, pixelRatio } = viewport;
  const radius = DOT_RADIUS_CSS * pixelRatio;
  const arm = Math.max(1, Math.round(CROSS_ARM_CSS * pixelRatio));
  const thickness = Math.max(1, Math.ceil(CROSS_WIDTH_CSS * pixelRatio));
  const reach = style === 'dots' ? radius : arm;
  const centers = (extent: number, translation: number, origin: number) =>
    worldPositions(extent, translation, origin, gap, pixelRatio, reach)
      .map(Math.round)
      .filter(center => center + reach > 0 && center - reach < extent);
  return {
    width: frame.width,
    height: frame.height,
    radius,
    arm,
    thickness,
    // Crosses keep their thin stroke's weight like the lines do.
    opacity:
      style === 'dots'
        ? 1
        : Math.min(1, (CROSS_WIDTH_CSS * pixelRatio) / thickness),
    x: centers(frame.width, translateX, frame.originX),
    y: centers(frame.height, translateY, frame.originY),
    position: frame.position,
    size: frame.size,
  };
}

function markPath(
  style: GridMarkStyle,
  geometry: NonNullable<ReturnType<typeof calculateGridMarkGeometry>>
) {
  const { x, y, radius: r, arm: a, thickness: t } = geometry;
  const half = Math.floor(t / 2);
  const mark =
    style === 'dots'
      ? (cx: number, cy: number) =>
          `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0Z`
      : (cx: number, cy: number) =>
          `M${cx - a} ${cy - half}h${2 * a}v${t}h${-2 * a}Z` +
          `M${cx - half} ${cy - a}h${t}v${2 * a}h${-t}Z`;
  const parts: string[] = [];
  for (const cy of y) for (const cx of x) parts.push(mark(cx, cy));
  return parts.join('');
}

/** The physical-pixel geometry EdgelessGrid draws: one path per visible tier. */
export function calculateEdgelessGrid(
  viewport: LayeredGridViewport,
  style: EdgelessGridStyle,
  colors: EdgelessGridColors,
  world: CanvasWorld
) {
  if (!Number.isFinite(viewport.zoom) || viewport.zoom <= 0) return null;
  const frame = physicalFrame({ ...viewport, gap: 1 });
  if (!frame) return null;
  const paths = [];
  for (const layer of planEdgelessGrid(viewport.zoom)) {
    // Hidden detail layers must not generate dense geometry at overview zoom.
    if (layer.opacity <= 0) continue;
    const tier = { ...viewport, gap: layer.screenGap };
    if (style === 'lines') {
      const geometry = calculateLineGridGeometry(tier, layer.lineWidth);
      if (!geometry) return null;
      const { lineWidth, x, y } = geometry;
      const { width, height } = frame;
      paths.push({
        level: layer.level,
        d: [
          ...x.map(left => `M${left} 0h${lineWidth}v${height}h-${lineWidth}Z`),
          ...y.map(top => `M0 ${top}h${width}v${lineWidth}h-${width}Z`),
        ].join(''),
        fill: colors.lines[layer.level].trim(),
        opacity: layer.opacity * geometry.opacity,
      });
      continue;
    }
    const geometry = calculateGridMarkGeometry(tier, style);
    if (!geometry) return null;
    paths.push({
      level: layer.level,
      d: markPath(style, geometry),
      fill: colors.mark.trim(),
      opacity: layer.opacity * geometry.opacity,
    });
  }
  // One filled path per layer avoids extra alpha at its own intersections.
  // Layers intentionally overlap, preserving CodeCanvas's visual hierarchy.
  if (paths.length === 0) return null;
  const worldX = (value: number) =>
    Math.round(
      (viewport.translateX + value * viewport.zoom) * viewport.pixelRatio +
        frame.originX
    );
  const worldY = (value: number) =>
    Math.round(
      (viewport.translateY + value * viewport.zoom) * viewport.pixelRatio +
        frame.originY
    );
  // An unbounded world (a projection) has no edge: the grid fills the view.
  const clip =
    world === 'bounded'
      ? {
          x: worldX(CANVAS_WORLD_MIN),
          y: worldY(CANVAS_WORLD_MIN),
          width: worldX(CANVAS_WORLD_MAX) - worldX(CANVAS_WORLD_MIN),
          height: worldY(CANVAS_WORLD_MAX) - worldY(CANVAS_WORLD_MIN),
        }
      : { x: 0, y: 0, width: frame.width, height: frame.height };
  return {
    width: frame.width,
    height: frame.height,
    paths,
    clip,
    position: frame.position,
    size: frame.size,
  };
}

let gridId = 0;

/**
 * The canvas grid in every style. A mounted SVG never enters the asynchronous
 * CSS background-image loader.
 */
export class EdgelessGrid {
  private readonly graphic: SVGSVGElement;
  private readonly clip: SVGRectElement;
  private readonly paths: Map<keyof EdgelessLineGridPalette, SVGPathElement>;

  private readonly spotlight: HTMLDivElement;

  constructor(
    host: HTMLElement,
    private readonly world: CanvasWorld = 'bounded'
  ) {
    const document = host.ownerDocument;
    const create = <K extends keyof SVGElementTagNameMap>(tag: K) =>
      document.createElementNS('http://www.w3.org/2000/svg', tag);
    this.graphic = create('svg');
    this.graphic.setAttribute('aria-hidden', 'true');
    this.graphic.setAttribute('data-edgeless-grid', '');
    this.graphic.style.cssText =
      'position:absolute;pointer-events:none;overflow:hidden';
    const defs = create('defs');
    const clipPath = create('clipPath');
    const clipId = `edgeless-grid-${++gridId}`;
    clipPath.id = clipId;
    this.clip = create('rect');
    clipPath.append(this.clip);
    defs.append(clipPath);
    const group = create('g');
    group.setAttribute('clip-path', `url(#${clipId})`);
    this.paths = new Map();
    for (const level of ['cell', 'block', 'sector'] as const) {
      const path = create('path');
      path.setAttribute('data-grid-level', level);
      path.setAttribute('opacity', '0');
      group.append(path);
      this.paths.set(level, path);
    }
    this.graphic.append(defs, group);
    this.spotlight = document.createElement('div');
    this.spotlight.setAttribute('data-edgeless-grid', 'spotlight');
    this.spotlight.style.cssText =
      'position:absolute;inset:0;pointer-events:none;background-repeat:no-repeat;background-size:100% 100%';
    // Preserve CSS image layering: the screen-space spotlight covers the grid.
    host.append(this.graphic, this.spotlight);
  }

  update(
    viewport: LayeredGridViewport,
    style: EdgelessGridStyle,
    colors: EdgelessGridColors
  ) {
    const grid = calculateEdgelessGrid(viewport, style, colors, this.world);
    // A transient zero-sized layout must not clear the last drawable grid.
    if (!grid) return;
    this.graphic.setAttribute('viewBox', `0 0 ${grid.width} ${grid.height}`);
    const [left, top] = grid.position.split(' ');
    const [width, height] = grid.size.split(' ');
    Object.assign(this.graphic.style, { left, top, width, height });
    for (const [key, value] of Object.entries(grid.clip)) {
      this.clip.setAttribute(key, String(value));
    }
    for (const [level, path] of this.paths) {
      const layer = grid.paths.find(layer => layer.level === level);
      if (layer) {
        path.setAttribute('d', layer.d);
        path.setAttribute('fill', layer.fill);
        path.setAttribute('opacity', String(layer.opacity));
      } else {
        path.setAttribute('opacity', '0');
      }
    }
  }

  setSpotlight(backgroundImage: string) {
    this.spotlight.style.backgroundImage = backgroundImage;
  }

  dispose() {
    this.graphic.remove();
    this.spotlight.remove();
  }
}
